import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IntegrationBase } from "@/features/Integrations/types/integration";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Plus, Search } from "lucide-react";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";

interface AgentIntegrationsProps {
  open: boolean;
  onClose: () => void;
  onSelect: (integration: IntegrationBase) => void;
  selectedIntegrations: string[];
}

export function AgentIntegrations({ 
  open, 
  onClose, 
  onSelect, 
  selectedIntegrations = []
}: AgentIntegrationsProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId;
  const [integrations, setIntegrations] = useState<IntegrationBase[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchIntegrations();
      setSearchQuery(""); // Limpa a busca ao abrir o menu
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIntegrations]); // Atualiza quando selectedIntegrations mudar também

  const fetchIntegrations = async () => {
    setIsLoading(true);
    try {
      const userOrganizationId = effectiveOrgId;
      
      if (!userOrganizationId) {
        console.error('User organization ID not available');
        setIntegrations([]);
        return;
      }

      // Parâmetros de filtro para buscar integrações base
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filterParams: any = {
        status: 'active'
      };


      // Busca todas as integrações base ativas da organização
      const allIntegrations = await IntegrationBaseService.getAll(filterParams);
      
      // Filtra apenas as integrações que não estão já selecionadas
      const availableIntegrations = allIntegrations.filter(
        (integration: IntegrationBase) => 
          !selectedIntegrations.includes(integration.id)
      );
      
      setIntegrations(availableIntegrations || []);
    } catch (error) {
      console.error("Erro ao buscar integrações:", error);
      setIntegrations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectIntegration = (integration: IntegrationBase) => {
    onSelect(integration);
    onClose();
  };

  const filteredIntegrations = integrations.filter((integration: IntegrationBase) => {
    const description = getIntegrationDescription(integration, i18n.language);
    return (integration.label || integration.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (description?.toLowerCase() || '').includes(searchQuery.toLowerCase());
  });

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>{t("agents.settings.integrations.selectTitle")}</SheetTitle>
        </SheetHeader>

        <div className="relative mt-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("agents.settings.integrations.searchPlaceholder")}
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <ScrollArea className="mt-6 h-[calc(100vh-8rem)]">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">
              {t('common.messages.loading')}
            </div>
          ) : filteredIntegrations.length > 0 ? (
            <div className="space-y-2">
              {filteredIntegrations.map((integration) => (
                <Card
                  key={integration.id}
                  className="cursor-pointer transition-colors hover:bg-muted"
                  onClick={() => handleSelectIntegration(integration)}
                >
                  <CardHeader className="flex flex-row items-center gap-4 space-y-0 p-4">
                    {integration?.logo_url ? (
                      <img 
                        src={integration.logo_url} 
                        alt={integration.label || integration.name || 'Integration'} 
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                        <span className="text-xl font-medium">
                          {(integration.label || integration.name || 'I')[0]?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <CardTitle className="text-base">
                        {integration.label || integration.name}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {getIntegrationDescription(integration, i18n.language) || t('agents.settings.integrations.noDescription')}
                      </CardDescription>
                    </div>
                    <div className="ml-4">
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              {searchQuery 
                ? t('agents.settings.integrations.noSearchResults')
                : t('agents.settings.integrations.noIntegrationsAvailable')
              }
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
